// src/resources/reports/ReportAppPenetration/index.tsx
//
// Drill-down por posto da seção "Penetração do app na pista" do Painel 360 —
// aqui entram valor, litros, abastecimentos, descontos, ticket médio e volume
// médio, cada um dentro/fora do app e o total da pista. Contrato e layout em
// docs/superpowers/specs/2026-09-17-penetracao-app-detalhe-design.md (§3/§4,
// pontuax-backend) e mockup aprovado no mesmo diretório.
import { Box, Chip, Typography } from '@mui/material';
import moment from 'moment';
import React from 'react';
import {
  AutocompleteArrayInput, AutocompleteInput, ChipField, DateField, Datagrid, Filter,
  ReferenceArrayField, ReferenceArrayInput, ReferenceInput, SelectArrayInput,
  SingleFieldList, useListContext, usePermissions, useTranslate,
} from 'react-admin';
import { Permission, ReportList } from '../../../components';
import { BetweenDatesInput } from '../../../components/BetweenDatesInput';
import RequiredChain from '../../../components/RequiredChain';
import { PERMISSIONS } from '../../../constants';
import { getChainId, getPlaceId } from '../../../lib';
import { filterToQuery, formatNumber } from '../../../utils';
import PenetrationTable from './PenetrationTable';
import SummaryCards from './SummaryCards';
import TrendChart from './TrendChart';
import { PenetrationDetailsData } from './types';
import { penetrationExporter } from './export';

const FiltersReportList = (props: any) => {
  const { permissions } = usePermissions();
  const translate = useTranslate();
  const { filterValues } = useListContext();

  return (
    <Filter {...props}>
      <BetweenDatesInput alwaysOn />
      {permissions && permissions.includes(PERMISSIONS.ADMIN) && (
        <ReferenceInput
          source="chainId"
          reference="chains"
          sort={{ field: 'name', order: 'ASC' }}
          // `null` (não `undefined`) é o que desliga o corte de 25 registros do
          // react-admin (useReferenceInputController) — o dataProvider só manda
          // `take` quando perPage é truthy. O tipo do react-admin só aceita
          // number, mas o componente aceita null em runtime; sem o cast a rede
          // com >25 postos perderia opções no filtro.
          perPage={null as unknown as number}
          alwaysOn
        >
          <AutocompleteInput
            filterToQuery={filterToQuery}
            label="Rede"
            style={{ minWidth: 180 }}
            defaultValue={getChainId()}
            optionText="name"
          />
        </ReferenceInput>
      )}
      <ReferenceArrayInput
        source="stationIds"
        reference="stations"
        perPage={null as unknown as number}
        filter={{ chainId: filterValues.chainId }}
        sort={{ field: 'name', order: 'ASC' }}
        alwaysOn
      >
        <AutocompleteArrayInput
          filterToQuery={filterToQuery}
          label="Posto"
          emptyText="Todos"
          optionText="name"
          sx={{ minWidth: 180, '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': { minWidth: 50 } }}
          noOptionsText={translate('ra.input.autocomplete.no_options_text')}
        />
      </ReferenceArrayInput>
      <ReferenceArrayInput
        source="fuelIds"
        reference="fuels"
        sort={{ field: 'name', order: 'ASC' }}
        alwaysOn
      >
        <SelectArrayInput
          label="Produto"
          optionText="name"
          style={{ minWidth: 160 }}
        />
      </ReferenceArrayInput>
    </Filter>
  );
};

const FilterTablePrint = () => {
  const { filterValues } = useListContext();
  return (
    <Datagrid bulkActionButtons={false} data={[{ ...filterValues, generatedReportAt: moment().toISOString() }]} total={1}>
      <DateField source="from" textAlign="center" label="De" sortable={false} locales="pt-BR" />
      <DateField source="to" textAlign="center" label="Até" sortable={false} locales="pt-BR" />
      <ReferenceArrayField label="Postos" emptyText="Todos" source="stationIds" textAlign="center" reference="stations">
        <SingleFieldList>
          <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>
      <ReferenceArrayField label="Produtos" emptyText="Todos" source="fuelIds" textAlign="center" reference="fuels">
        <SingleFieldList>
          <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>
      <DateField source="generatedReportAt" textAlign="center" label="Gerado às" showTime sortable={false} locales="pt-BR" />
    </Datagrid>
  );
};

const PenetrationDetailsReport = () => {
  const { data } = useListContext();
  const details = data?.[0] as unknown as PenetrationDetailsData | undefined;

  if (!details) return null;

  if (!details.available) {
    return (
      <Box sx={{ p: 5, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Dados da pista indisponíveis para esta rede (sem concentrador integrado).
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <SummaryCards totals={details.totals} />
      <TrendChart series={details.series} />
      <PenetrationTable totals={details.totals} byStation={details.byStation} />

      {details.fuelTypesIgnored.length > 0 && (
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 1.5 }}>
          Sem correspondência na pista: <b>{details.fuelTypesIgnored.join(', ')}</b> — esse(s) combustível(is) não existe(m) no sistema da bomba, então ficam fora da comparação acima.
        </Typography>
      )}

      {details.excludedStations.length > 0 && (
        <Box sx={{ mt: 2, p: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff' }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 0.5 }}>Sem dados da pista no período</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
            Estes postos têm abastecimento no app, mas o concentrador não envia os dados da pista — por isso ficam fora do cálculo acima.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {details.excludedStations.map(station => (
              <Chip
                key={station.placeId}
                size="small"
                label={`${station.name} · ${formatNumber(station.appFillins)} no app`}
                sx={{ bgcolor: '#f1f5f9' }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

// props: any — ReportList (componente legado, sem tipos) infere o shape de
// props exigido a partir de UM dos call sites; espalhar `{...props}`, como
// ReportFillinByStation.jsx e ReportDotzRefuelTransactions.tsx já fazem, evita
// TS2740 (propriedades "faltando" que na real nunca são passadas por ninguém).
const ReportAppPenetration = (props: any) => {
  const placeId = getPlaceId();

  const filterValues: Record<string, any> = {
    chainId: getChainId(),
    from: moment().startOf('day').subtract(1, 'month').toISOString(),
    to: moment().endOf('day').toISOString(),
  };

  if (placeId) {
    filterValues.stationIds = [placeId];
  }

  return (
    <Permission permission={PERMISSIONS.DASHBOARD_V2}>
      <RequiredChain>
        <ReportList periodRequired
          {...props}
          title="Penetração do app por posto"
          resource="dashboard-v2/penetration-details"
          filters={<FiltersReportList />}
          sort={{ field: 'id', order: 'ASC' }}
          filterDefaultValues={filterValues}
          showPagination={false}
          bulkActionButtons={false}
          customExporter={penetrationExporter}
          titleOnPrint="Relatório de Penetração do App por Posto"
          filterTablePrint={<FilterTablePrint />}
          landscape
        >
          <PenetrationDetailsReport />
        </ReportList>
      </RequiredChain>
    </Permission>
  );
};

export default ReportAppPenetration;
